/**
 * StepPaths - Composant pour afficher les paths d'une étape
 * Extrait de BaseStepWidget pour améliorer la maintenabilité
 */

import React, {useState, useRef, useEffect, useMemo} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Animated,
  Easing,
} from 'react-native';
import {PlayerStep} from '../models/PlayerStep';
import {PlayerPath} from '../models/PlayerPath';
import {GameState} from '../models/GameState';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faArrowRight, faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {Fonts} from '../config/fonts';
import conditionHelper from '../helpers/ConditionHelper';
import GameRegistry from '../systems/GameRegistry';
import {useTheme} from '../contexts/ThemeContext';
import {Theme} from '../theme/theme';

interface StepPathsProps {
  step: PlayerStep;
  gameState: GameState | undefined;
  editorMode: boolean;
  onPathPress: (pathIndex: number) => void;
  onShowConditions: (path: PlayerPath, conditions: string[]) => void;
}

const StepPaths: React.FC<StepPathsProps> = ({
  step,
  gameState,
  editorMode,
  onPathPress,
  onShowConditions,
}) => {
  const {theme, isReadingMode} = useTheme();
  const [hoveredPathIndex, setHoveredPathIndex] = useState<number | null>(null);
  
  // Générer les styles basés sur le thème ET mode lecture
  const styles = useMemo(() => createStyles(theme, isReadingMode), [theme, isReadingMode]);
  
  // Obtenir les paths originaux (tous les paths avant filtrage) pour le mode éditeur
  const originalPaths = step.allPaths || step.paths || [];
  
  // Utiliser useMemo pour forcer le recalcul quand editorMode change
  const availablePaths = useMemo(() => {
    if (!gameState) {
      return originalPaths;
    }
    const cheatValue = Boolean(editorMode);
    
    // Créer un step temporaire avec tous les paths originaux pour filterPaths
    const stepWithAllPaths = {
      ...step,
      paths: originalPaths,
    };
    
    return conditionHelper.filterPaths(stepWithAllPaths, gameState, cheatValue);
  }, [gameState, step, editorMode, originalPaths]);
  
  // Détecter les paths cachés (en mode éditeur, on affiche tous les paths mais on marque ceux qui sont normalement cachés)
  const getHiddenPaths = (): Set<number> => {
    if (!gameState || !editorMode) {
      return new Set();
    }
    
    // Créer un step temporaire avec tous les paths originaux pour filterPaths
    const stepWithAllPaths = {
      ...step,
      paths: originalPaths,
    };
    
    // Filtrer normalement (sans cheat) pour identifier les paths cachés
    const filteredPaths = conditionHelper.filterPaths(stepWithAllPaths, gameState, false);
    const hiddenIndices = new Set<number>();
    
    // Comparer originalPaths (tous les paths) avec filteredPaths (paths disponibles normalement)
    originalPaths.forEach((originalPath) => {
      // Vérifier si ce path est dans les paths filtrés
      const isAvailable = filteredPaths.some(
        (fp) => (fp.id && originalPath.id && fp.id === originalPath.id) || 
                (fp.description === originalPath.description && fp.child === originalPath.child)
      );
      
      if (!isAvailable) {
        // Ce path est normalement caché, trouver son index dans availablePaths
        const indexInAvailable = availablePaths.findIndex(
          (ap) => (ap.id && originalPath.id && ap.id === originalPath.id) ||
                  (ap.description === originalPath.description && ap.child === originalPath.child)
        );
        if (indexInAvailable !== -1) {
          hiddenIndices.add(indexInAvailable);
        }
      }
    });
    
    return hiddenIndices;
  };
  
  const hiddenPathIndices = useMemo(() => getHiddenPaths(), [availablePaths, originalPaths, gameState, editorMode]);
  
  // Obtenir les messages de conditions pour un path
  const getConditionMessages = (path: PlayerPath): string[] => {
    if (!path.conditions || path.conditions.length === 0) {
      return [];
    }
    
    const messages: string[] = [];
    const conditionTree = new Map<number, typeof path.conditions>();
    
    // Grouper les conditions par groupNumber
    path.conditions.forEach((condition) => {
      const groupNumber = condition.groupNumber ?? 0;
      if (!conditionTree.has(groupNumber)) {
        conditionTree.set(groupNumber, []);
      }
      conditionTree.get(groupNumber)!.push(condition);
    });
    
    // Pour chaque groupe, créer un message
    conditionTree.forEach((group, groupNumber) => {
      const groupMessages = group.map((condition) => {
        const conditionType = GameRegistry.getConditionType(condition.typeName);
        if (conditionType && conditionType.getDescription) {
          return conditionType.getDescription(condition);
        }
        // Fallback : description basique
        return `${condition.typeName}(${condition.attribute || '?'}, ${condition.value || '?'})`;
      });
      
      if (groupMessages.length > 0) {
        if (conditionTree.size > 1) {
          messages.push(`Groupe ${groupNumber + 1}: ${groupMessages.join(' ET ')}`);
        } else {
          messages.push(...groupMessages);
        }
      }
    });
    
    return messages;
  };
  
  // Créer les animations pour les paths
  const [pathsAnimations, setPathsAnimations] = useState<Array<{
    opacity: Animated.Value;
    translateY: Animated.Value;
    scale: Animated.Value;
  }>>([]);
  
  useEffect(() => {
    const newPathsAnimations = availablePaths.map(() => ({
      opacity: new Animated.Value(0),
      translateY: new Animated.Value(30),
      scale: new Animated.Value(0.95),
    }));
    setPathsAnimations(newPathsAnimations);
  }, [step.numero, availablePaths.length]);
  
  // Lancer les animations après qu'elles soient créées
  useEffect(() => {
    if (pathsAnimations.length === 0) {
      return;
    }
    
    // Animer les paths en décalé (delay: 200ms * index, duration: 800ms)
    pathsAnimations.forEach((anim, index) => {
      Animated.parallel([
        Animated.timing(anim.opacity, {
          toValue: 1,
          duration: 800,
          delay: 200 * index,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(anim.translateY, {
          toValue: 0,
          duration: 800,
          delay: 200 * index,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(anim.scale, {
          toValue: 1,
          duration: 800,
          delay: 200 * index,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, [step.numero, pathsAnimations.length]);
  
  if (availablePaths.length === 0) {
    return null;
  }
  
  return (
    <View style={styles.decisionZone}>
      <View style={styles.decisionZoneContent}>
        {availablePaths.map((path, index) => {
          const anim = pathsAnimations[index];
          const isLast = index === availablePaths.length - 1;
          const pathText = path.title || path.description;
          const isHidden = hiddenPathIndices.has(index);
          const conditionMessages = isHidden ? getConditionMessages(path) : [];
          const isHovered = hoveredPathIndex === index;
          
          if (!anim) {
            // Fallback si l'animation n'est pas encore créée
            return (
              <View key={path.id || index} style={styles.pathContainer}>
                <TouchableOpacity
                  style={[
                    styles.pathButton, 
                    isLast && styles.pathButtonLast,
                    isHovered && styles.pathButtonHovered,
                    isHidden && styles.pathButtonHidden
                  ]}
                  activeOpacity={0.7}
                  onPress={() => onPathPress(index)}
                  {...(Platform.OS === 'web' && {
                    onMouseEnter: () => setHoveredPathIndex(index),
                    onMouseLeave: () => setHoveredPathIndex(null),
                  })}>
                  <View style={styles.pathButtonContent}>
                    <View style={styles.pathButtonLeft}>
                      <View style={[
                        styles.pathButtonIndicator,
                        isHovered && styles.pathButtonIndicatorHovered,
                        isHidden && styles.pathButtonIndicatorHidden
                      ]} />
                      <Text style={[
                        styles.pathText,
                        isHovered && styles.pathTextHovered,
                        isHidden && styles.pathTextHidden
                      ]}>
                        {pathText}
                      </Text>
                    </View>
                    <View style={styles.pathButtonRight}>
                      {isHidden && conditionMessages.length > 0 && (
                        <TouchableOpacity
                          style={styles.pathInfoButton}
                          onPress={(e) => {
                            e.stopPropagation();
                            onShowConditions(path, conditionMessages);
                          }}
                          {...(Platform.OS === 'web' && {
                            onMouseEnter: () => {},
                            onMouseLeave: () => {},
                          })}>
                          <FontAwesomeIcon 
                            icon={faInfoCircle} 
                            size={14} 
                            color={theme.colors.accent} 
                          />
                        </TouchableOpacity>
                      )}
                      <FontAwesomeIcon 
                        icon={faArrowRight} 
                        size={18} 
                        color={theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
                          ? '#A68968' // Dark mode: doré
                          : '#8B7355'} // Light mode: marron/doré
                        style={[
                          styles.pathArrow,
                          isHovered && styles.pathArrowHovered
                        ]}
                      />
                    </View>
                  </View>
                </TouchableOpacity>
              </View>
            );
          }
          
          return (
            <Animated.View
              key={path.id || index}
              style={[
                styles.pathContainer,
                {
                  opacity: anim.opacity,
                  transform: [
                    {translateY: anim.translateY},
                    {scale: anim.scale},
                  ],
                },
              ]}>
              <TouchableOpacity
                style={[
                  styles.pathButton, 
                  isLast && styles.pathButtonLast,
                  isHovered && styles.pathButtonHovered,
                  isHidden && styles.pathButtonHidden
                ]}
                activeOpacity={0.7}
                onPress={() => onPathPress(index)}
                {...(Platform.OS === 'web' && {
                  onMouseEnter: () => setHoveredPathIndex(index),
                  onMouseLeave: () => setHoveredPathIndex(null),
                })}>
                <View style={styles.pathButtonContent}>
                  <View style={styles.pathButtonLeft}>
                    <View style={[
                      styles.pathButtonIndicator,
                      isHovered && styles.pathButtonIndicatorHovered,
                      isHidden && styles.pathButtonIndicatorHidden
                    ]} />
                    <Text style={[
                      styles.pathText,
                      isHovered && styles.pathTextHovered,
                      isHidden && styles.pathTextHidden
                    ]}>
                      {pathText}
                    </Text>
                  </View>
                  <View style={styles.pathButtonRight}>
                    {isHidden && conditionMessages.length > 0 && (
                      <TouchableOpacity
                        style={styles.pathInfoButton}
                        onPress={(e) => {
                          e.stopPropagation();
                          onShowConditions(path, conditionMessages);
                        }}
                        {...(Platform.OS === 'web' && {
                          onMouseEnter: () => {},
                          onMouseLeave: () => {},
                        })}>
                        <FontAwesomeIcon 
                          icon={faInfoCircle} 
                          size={14} 
                          color="#8B7355" 
                        />
                      </TouchableOpacity>
                    )}
                    <FontAwesomeIcon 
                      icon={faArrowRight} 
                      size={18} 
                      color={theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
                        ? '#A68968' // Dark mode: doré
                        : '#8B7355'} // Light mode: marron/doré
                      style={[
                        styles.pathArrow,
                        isHovered && styles.pathArrowHovered
                      ]}
                    />
                  </View>
                </View>
              </TouchableOpacity>
            </Animated.View>
          );
        })}
      </View>
    </View>
  );
};

// Fonction pour créer les styles basés sur le thème ET mode lecture
const createStyles = (theme: Theme, isReadingMode: boolean) => StyleSheet.create({
  decisionZone: {
    marginTop: 0,
    paddingTop: isReadingMode ? theme.spacing.xxl + 8 : theme.spacing.lg,
    paddingBottom: isReadingMode ? theme.spacing.xl : theme.spacing.lg,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
    borderTopWidth: 0, // Pas de bordure
    borderTopColor: 'transparent',
  },
  decisionZoneContent: {
    // Contenu de la zone de décision
  },
  pathContainer: {
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  pathButton: {
    backgroundColor: isReadingMode 
      ? 'transparent' 
      : (theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
        ? 'rgba(44, 40, 36, 0.9)' // Dark mode: marron foncé
        : 'rgba(255, 252, 248, 0.95)'), // Light mode: ivoire/beige
    padding: 0,
    marginBottom: isReadingMode ? theme.spacing.xl : theme.spacing.md,
    borderRadius: isReadingMode ? 0 : 16, // Plus arrondi
    borderWidth: isReadingMode ? 0 : 1,
    borderColor: isReadingMode 
      ? 'transparent' 
      : (theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
        ? 'rgba(90, 82, 74, 0.3)' // Dark mode
        : 'rgba(212, 196, 176, 0.4)'), // Light mode
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: isReadingMode 
          ? 'none' 
          : (theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
            ? '0 3px 8px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.03)' // Dark
            : '0 3px 8px rgba(139, 115, 85, 0.18), 0 1px 3px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.6)'), // Light - effet relief
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      },
      default: {
        shadowColor: isReadingMode ? 'transparent' : (theme.colors.bg === '#1A1614' ? '#000' : '#8B7355'),
        shadowOffset: {width: 0, height: isReadingMode ? 0 : 3},
        shadowOpacity: isReadingMode ? 0 : 0.18,
        shadowRadius: isReadingMode ? 0 : 4,
        elevation: isReadingMode ? 0 : 3,
      },
    }),
  },
  pathButtonHovered: {
    backgroundColor: isReadingMode 
      ? 'transparent'
      : (theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
        ? 'rgba(50, 45, 40, 0.95)' // Dark mode: un peu plus clair
        : 'rgba(248, 245, 240, 1)'), // Light mode: légèrement plus lumineux
    borderRadius: isReadingMode ? 0 : 16,
    ...Platform.select({
      web: {
        boxShadow: isReadingMode 
          ? 'none' 
          : (theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
            ? '0 4px 12px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.05)' // Dark
            : '0 4px 12px rgba(139, 115, 85, 0.22), 0 2px 4px rgba(0, 0, 0, 0.12), inset 0 1px 1px rgba(255, 255, 255, 0.7)'), // Light
        transform: 'translateY(-1px)',
      },
      default: {
        shadowColor: isReadingMode ? 'transparent' : (theme.colors.bg === '#1A1614' ? '#000' : '#8B7355'),
        shadowOffset: {width: 0, height: isReadingMode ? 0 : 4},
        shadowOpacity: isReadingMode ? 0 : 0.22,
        shadowRadius: isReadingMode ? 0 : 6,
        elevation: isReadingMode ? 0 : 4,
      },
    }),
  },
  pathButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: isReadingMode ? 0 : 20, // Plus d'espace à gauche
    paddingRight: isReadingMode ? 0 : 24, // Plus d'espace à droite pour la flèche
    paddingVertical: isReadingMode ? theme.spacing.md : 18, // Padding vertical généreux
    minHeight: isReadingMode ? 48 : 64, // Plus haut
  },
  pathButtonLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: isReadingMode ? 0 : theme.spacing.md,
    paddingLeft: 0,
  },
  pathButtonIndicator: {
    width: isReadingMode ? 0 : 4, // Barre fine comme sur la maquette
    height: '100%',
    backgroundColor: theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
      ? '#A68968' // Dark mode: doré clair
      : '#8B7355', // Light mode: marron/doré
    borderRadius: 0,
    flexShrink: 0,
    opacity: isReadingMode ? 0 : 1, // Opaque
    ...Platform.select({
      web: {
        transition: 'all 0.2s ease',
        boxShadow: theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
          ? '1px 0 2px rgba(0,0,0,0.3)' // Dark
          : '1px 0 2px rgba(139, 115, 85, 0.25)', // Light
      },
    }),
  },
  pathButtonIndicatorHovered: {
    // AUCUN changement au hover
  },
  pathButtonRight: {
    marginLeft: isReadingMode ? theme.spacing.sm : theme.spacing.md,
    flexShrink: 0,
  },
  pathArrow: {
    opacity: isReadingMode ? 0.35 : 0.5, // Visible mais discret
    color: theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
      ? '#A68968' // Dark mode: doré
      : '#8B7355', // Light mode: marron/doré (assortie à la barre)
    ...Platform.select({
      web: {
        transition: 'transform 0.2s ease, opacity 0.2s ease',
      },
    }),
  },
  pathArrowHovered: {
    // AUCUN changement au hover
  },
  pathButtonLast: {
    marginBottom: 0,
  },
  pathText: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
      ? 'rgba(220, 210, 195, 0.9)' // Dark mode: beige clair
      : 'rgba(90, 74, 58, 0.85)', // Light mode: marron grisé
    flex: 1,
    textAlign: 'left',
    lineHeight: 16 * 1.5,
    fontFamily: isReadingMode ? Fonts.reading : Fonts.narrative,
    letterSpacing: 0.2,
    ...Platform.select({
      web: {
        transition: 'color 0.2s ease',
      },
      default: {},
    }),
  },
  pathTextHovered: {
    color: theme.colors.bg === '#1A1614' || theme.colors.bg === '#141210'
      ? 'rgba(232, 226, 218, 0.95)' // Dark mode: plus clair au hover
      : 'rgba(70, 56, 42, 0.95)', // Light mode: plus foncé au hover
    fontWeight: '500',
  },
  pathTextHidden: {
    opacity: 0.6,
  },
  pathButtonHidden: {
    opacity: 0.6,
    borderStyle: 'dashed',
    borderWidth: 1.5,
    borderColor: theme.colors.accent,
  },
  pathButtonIndicatorHidden: {
    opacity: 0.5,
  },
  pathInfoButton: {
    marginRight: theme.spacing.sm,
    padding: theme.spacing.xs,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.objectBg,
    opacity: isReadingMode ? 0.6 : 1, // Plus discret en mode lecture
  },
});

export default StepPaths;
